import React, { useState } from 'react';
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography,
    Tooltip,
} from '@mui/material';
import { ExpandLess, ExpandMore, FiberManualRecord } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MenuItem } from '../../config/menuConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApplication } from '../../contexts/ApplicationContext';

interface SidebarItemProps {
    item: MenuItem;
    level?: number;
    collapsed?: boolean;
    forceOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ item, level = 0, collapsed = false, forceOpen = false }) => {
    const location = useLocation();
    const { t } = useLanguage();
    const { currentApp } = useApplication();
    const [open, setOpen] = useState(false);
    const hasChildren = item.child && item.child.length > 0;

    // Auto-expand if forceOpen is true
    React.useEffect(() => {
        if (forceOpen) {
            setOpen(true);
        }
    }, [forceOpen]);

    const handleClick = () => {
        if (hasChildren && !collapsed) {
            setOpen(!open);
        }
    };

    const isExternal = item.href?.startsWith('http');
    // It's a link if it has no children AND (it's not a header OR it has an explicit href)
    // Actually, if it has an href, it should be a link regardless of type, unless it has children acting as a folder
    const isLink = !hasChildren && !!item.href;

    // Check if active
    const isActive = isLink && !isExternal && (
        item.href === location.pathname ||
        (item.href !== '/' && location.pathname.startsWith(item.href || ''))
    );

    const paddingLeft = collapsed ? 1 : level * 2 + 2;

    const renderIcon = () => {
        if (item.icon) {
            if (typeof item.icon === 'string') {
                return <i className={item.icon} style={{ fontSize: '1.2rem', minWidth: '24px' }}></i>;
            }
            return item.icon;
        }
        return <FiberManualRecord sx={{ fontSize: 8, opacity: 0.5 }} />;
    };

    // Headers in this app can be links or parents, so we don't return early.
    // Instead we style them differently if needed.
    const isHeader = item.type === 'header';

    const content = (
        <ListItemButton
            onClick={handleClick}
            component={isLink ? (isExternal ? 'a' : RouterLink) : 'div'}
            href={isLink && isExternal ? item.href : undefined}
            to={
                isLink && !isExternal
                    ? (item.href?.startsWith('/') && currentApp && !item.href.startsWith(`/${currentApp}`)
                        ? `/${currentApp}${item.href}`
                        : item.href || '')
                    : undefined
            }
            target={isLink && isExternal ? '_blank' : undefined}
            className={`menu-item-base ${isActive ? 'menu-item-active' : ''} ${isHeader ? 'menu-header-item' : ''}`}
            selected={isActive}
            sx={{
                pl: collapsed ? 'auto' : paddingLeft,
                justifyContent: collapsed ? 'center' : 'initial',
                // Special styling for headers if they are just separators? 
                // But here they seem to be main nav items.
                // We'll keep them consistent with other items but maybe bold?
            }}
        >
            <ListItemIcon sx={{
                color: isActive ? 'inherit' : (isHeader ? '#ffffff' : 'inherit'), // Headers brighter?
                minWidth: collapsed ? 0 : 32,
                mr: collapsed ? 0 : 1,
                justifyContent: 'center',
                '& svg': { fontSize: '1.25rem' }
            }}>
                {renderIcon()}
            </ListItemIcon>
            {!collapsed && (
                <>
                    <ListItemText
                        primary={item.translationKey ? t(item.translationKey) : item.caption}
                        primaryTypographyProps={{
                            variant: 'body2', // Use body2 for all to keep pills consistent
                            noWrap: true,
                            sx: {
                                fontWeight: isActive || isHeader ? 600 : 400,
                                letterSpacing: isHeader ? '0.5px' : 'normal',
                                textTransform: isHeader ? 'uppercase' : 'none',
                                fontSize: isHeader ? '0.75rem' : '0.875rem'
                            }
                        }}
                    />
                    {hasChildren && (open ? <ExpandLess sx={{ fontSize: '1rem', opacity: 0.7 }} /> : <ExpandMore sx={{ fontSize: '1rem', opacity: 0.7 }} />)}
                </>
            )}
        </ListItemButton>
    );

    return (
        <>
            <Tooltip title={collapsed ? (item.translationKey ? t(item.translationKey) : item.caption) : ""} placement="right">
                {content}
            </Tooltip>
            {hasChildren && !collapsed && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {item.child!.map((child, index) => (
                            <SidebarItem key={index} item={child} level={level + 1} collapsed={collapsed} forceOpen={forceOpen} />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

export default SidebarItem;
